import React from 'react';
import { Box, Paper, Chip } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useChat } from '../../context/chatContext';

import { ChatMessage as ChatMessageType } from '../../types';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import MessageThoughts from './chatMessageThoughts';

interface ChatMessageProps {
  message: ChatMessageType;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.sender === 'user';
  const { toggleSettingsModal, thoughtSteps, toggleShowAds } = useChat();

  // Derive once per render; no setState in render.
  const steps = (message.thoughts && message.thoughts.length)
    ? message.thoughts
    : (thoughtSteps ?? []);

  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 3, mt: isUser ? 2 : 0 }}>
      <Box sx={{ display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'flex-start' }}>
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            bgcolor: '#242424',
            maxWidth: '600px',
            wordWrap: 'break-word',
            '& pre': {
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: '8px' },
              '&::-webkit-scrollbar-track': { backgroundColor: grey[300], borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: grey[500],
                borderRadius: '4px',
                '&:hover': { backgroundColor: grey[600] },
              },
            },
          }}
        >
          {message.text && (
            <Box
              sx={{
                color: (message.upgradePrompt || message.adsPrompt) ? 'error.main' : (isUser ? grey[300] : grey[100]),
                cursor: (message.upgradePrompt || message.adsPrompt) ? 'pointer' : 'default',
                '& h1': { fontSize: '1.5rem', fontWeight: 700, mb: 1.0 },
                '& h2': { fontSize: '1.25rem', fontWeight: 700, mt: 1.5, mb: 0.75 },
                '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 1.25, mb: 0.5 },
                '& p': { mb: 1 },
                '& ul, & ol': { pl: 3, mb: 1 },
                '& code': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', px: 0.5, py: 0.2, borderRadius: 0.5 },
                '& pre': { p: 1, borderRadius: 1, bgcolor: grey[800] },
                textDecoration: (message.upgradePrompt || message.adsPrompt) ? 'underline' : 'none',
              }}
              onClick={() => {
                if (message.adsPrompt) toggleShowAds(true);
                else if (message.upgradePrompt) toggleSettingsModal(true, 'account');
              }}
            >
              <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {(message.text && message.text.replace(
                  /([^\n])\n(\|[^|\n]+\|[^\n]*\n\|[-:| ]+\|)/g,
                  '$1\n\n$2'
                )) ?? ''}
              </Markdown>
            </Box>
          )}

          {!!steps?.length && !isUser && <MessageThoughts steps={steps} />}

          {message.attachments?.map((file, idx) => (
            <Box key={idx} sx={{ mt: 1 }}>
              {file.type?.startsWith('image/') ? (
                <Box component="img" src={file.dataUrl} alt={file.name} sx={{ maxWidth: '100%', borderRadius: 1 }} />
              ) : (
                <Chip
                  label={file.name}
                  component="a"
                  href={file.dataUrl}
                  download={file.name}
                  clickable
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
          ))}
        </Paper>
      </Box>
    </Box>
  );
};

export default ChatMessage;
